import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Globe, Megaphone, Pause, Play, Search, Sparkles, Wand2, Workflow,
} from 'lucide-react';

const SCENE_MS = 4200;

const SCENES = [
  {
    id: 'build',
    icon: Wand2,
    accent: 'from-violet-500 to-indigo-500',
    chip: 'Build',
    title: 'AI websites in minutes',
    line: 'Describe the business → Snag Builder generates pages, copy, and a live preview.',
    ui: 'builder',
  },
  {
    id: 'leads',
    icon: Search,
    accent: 'from-lime-500 to-emerald-500',
    chip: 'Grow',
    title: 'Lead Scraper that fills CRM',
    line: 'Find local & B2B leads, enrich contacts, then sync straight into your pipeline.',
    ui: 'leads',
  },
  {
    id: 'market',
    icon: Megaphone,
    accent: 'from-fuchsia-500 to-pink-500',
    chip: 'Market',
    title: 'Email · SMS · WhatsApp',
    line: 'Run campaigns across channels from one Marketing Suite — same contacts, one inbox.',
    ui: 'market',
  },
  {
    id: 'automate',
    icon: Workflow,
    accent: 'from-cyan-500 to-sky-500',
    chip: 'Automate',
    title: 'Automations & AI agents',
    line: 'Trigger follow-ups, qualify leads, and keep outreach running while you sleep.',
    ui: 'automate',
  },
  {
    id: 'own',
    icon: Globe,
    accent: 'from-amber-500 to-orange-500',
    chip: 'Own',
    title: 'Domains & publish',
    line: 'Register a domain, attach hosting, and ship — white-label ready for agencies.',
    ui: 'own',
  },
];

function FakeUi({ kind, light = false }) {
  const border = light ? 'border-stone-200' : 'border-white/10';
  const panel = light ? 'bg-stone-50' : 'bg-zinc-900';
  const ink = light ? 'text-stone-800' : 'text-white';
  const muted = light ? 'text-stone-500' : 'text-zinc-400';
  const faint = light ? 'text-stone-400' : 'text-zinc-500';

  if (kind === 'builder') {
    return (
      <div className="h-full flex flex-col gap-2 p-3">
        <div className="flex gap-2">
          <div className={`flex-1 rounded-lg ${panel} border ${border} p-2.5 text-left`}>
            <p className={`text-[9px] uppercase tracking-wider ${faint} mb-1`}>Prompt</p>
            <motion.p
              key="prompt"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`text-[11px] leading-snug ${light ? 'text-stone-700' : 'text-zinc-200'}`}
            >
              “Modern bakery site with menu, booking, and Instagram feed…”
            </motion.p>
          </div>
          <div className={`w-[38%] rounded-lg border p-2 flex flex-col justify-center ${
            light ? 'border-[#FF6B4A]/30 bg-[#FFF1EC]' : 'border-violet-500/30 bg-violet-500/10'
          }`}>
            <p className={`text-[9px] ${light ? 'text-[#E24B2D]' : 'text-violet-300'}`}>Generating</p>
            <div className={`mt-1.5 h-1.5 rounded-full overflow-hidden ${light ? 'bg-stone-200' : 'bg-zinc-800'}`}>
              <motion.div
                className={`h-full bg-gradient-to-r ${light ? 'from-[#FF6B4A] to-[#14B8A6]' : 'from-violet-500 to-fuchsia-400'}`}
                initial={{ width: '8%' }}
                animate={{ width: ['8%', '92%', '100%'] }}
                transition={{ duration: 3.2, ease: 'easeInOut' }}
              />
            </div>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-3 gap-2 min-h-0">
          {['Hero', 'Menu', 'Book'].map((label, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 + i * 0.25 }}
              className={`rounded-lg border p-2 ${
                light
                  ? 'border-stone-200 bg-white'
                  : 'border-white/10 bg-gradient-to-br from-zinc-800/80 to-zinc-950'
              }`}
            >
              <div className={`h-10 rounded-md mb-1.5 bg-gradient-to-br ${
                light
                  ? (i === 0 ? 'from-[#FF6B4A]/25 to-stone-100' : i === 1 ? 'from-amber-400/25 to-stone-100' : 'from-teal-400/25 to-stone-100')
                  : (i === 0 ? 'from-violet-600/40 to-zinc-900' : i === 1 ? 'from-amber-600/30 to-zinc-900' : 'from-emerald-600/30 to-zinc-900')
              }`} />
              <p className={`text-[10px] ${muted}`}>{label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  if (kind === 'leads') {
    const rows = [
      { name: 'Blue Harbor Café', score: '9.2', city: 'Austin' },
      { name: 'Northline Dental', score: '8.7', city: 'Dallas' },
      { name: 'Peak Fitness Co', score: '8.4', city: 'Denver' },
    ];
    return (
      <div className="h-full p-3 flex flex-col gap-2">
        <div className={`flex items-center gap-2 rounded-lg border ${border} ${panel} px-2.5 py-2`}>
          <Search className={`w-3.5 h-3.5 ${light ? 'text-teal-600' : 'text-lime-400'}`} />
          <motion.span
            className={`text-[11px] ${light ? 'text-stone-600' : 'text-zinc-300'}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            bakeries near Austin · ICP score ≥ 8
          </motion.span>
        </div>
        <div className="flex-1 space-y-1.5 overflow-hidden">
          {rows.map((r, i) => (
            <motion.div
              key={r.name}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + i * 0.35 }}
              className={`flex items-center justify-between rounded-lg border px-2.5 py-2 ${
                light ? 'border-stone-200 bg-white' : 'border-white/10 bg-white/[0.04]'
              }`}
            >
              <div className="text-left">
                <p className={`text-[11px] font-medium ${ink}`}>{r.name}</p>
                <p className={`text-[10px] ${faint}`}>{r.city}</p>
              </div>
              <span className={`text-[10px] font-semibold ${light ? 'text-teal-600' : 'text-lime-400'}`}>{r.score}</span>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  if (kind === 'market') {
    const channels = [
      { label: 'Email', pct: 72, color: light ? 'bg-[#FF6B4A]' : 'bg-violet-500' },
      { label: 'SMS', pct: 54, color: light ? 'bg-teal-500' : 'bg-cyan-500' },
      { label: 'WhatsApp', pct: 88, color: light ? 'bg-emerald-500' : 'bg-emerald-500' },
    ];
    return (
      <div className="h-full p-3 flex flex-col justify-center gap-3">
        {channels.map((c, i) => (
          <div key={c.label} className="text-left">
            <div className="flex justify-between text-[10px] mb-1">
              <span className={muted}>{c.label}</span>
              <span className={light ? 'text-stone-600' : 'text-zinc-300'}>{c.pct}% open/reply</span>
            </div>
            <div className={`h-2 rounded-full overflow-hidden ${light ? 'bg-stone-200' : 'bg-zinc-800'}`}>
              <motion.div
                className={`h-full ${c.color}`}
                initial={{ width: 0 }}
                animate={{ width: `${c.pct}%` }}
                transition={{ delay: 0.25 + i * 0.2, duration: 1.1, ease: 'easeOut' }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (kind === 'automate') {
    const steps = ['New lead', 'Score ICP', 'WhatsApp ping', 'Book call'];
    return (
      <div className="h-full p-3 flex items-center justify-center">
        <div className="flex flex-wrap items-center justify-center gap-2 max-w-sm">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: i * 0.35 }}
                className={`rounded-full border px-3 py-1.5 text-[11px] ${
                  light
                    ? 'border-teal-300 bg-teal-50 text-teal-700'
                    : 'border-cyan-500/40 bg-cyan-500/10 text-cyan-200'
                }`}
              >
                {s}
              </motion.div>
              {i < steps.length - 1 ? (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 + i * 0.35 }}
                  className={`text-xs ${faint}`}
                >
                  →
                </motion.span>
              ) : null}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-3 flex flex-col gap-2">
      <div className={`rounded-lg border px-3 py-2 text-left ${
        light ? 'border-amber-300 bg-amber-50' : 'border-amber-500/30 bg-amber-500/10'
      }`}>
        <p className={`text-[10px] ${light ? 'text-amber-700' : 'text-amber-300'}`}>Domain ready</p>
        <p className={`text-sm font-semibold tracking-tight ${ink}`}>yourbrand.com</p>
      </div>
      <div className="flex-1 grid grid-cols-2 gap-2">
        {['DNS live', 'SSL on', 'CDN', 'Published'].map((label, i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.2 }}
            className={`rounded-lg border flex items-center justify-center text-[11px] ${
              light
                ? 'border-stone-200 bg-white text-stone-600'
                : 'border-white/10 bg-white/[0.04] text-zinc-300'
            }`}
          >
            {label}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/**
 * Looping product explainer framed like a short video — drop-in for the landing hero.
 */
export default function FeatureExplainerVideo({ brand = 'Snagcart', className = '', variant = 'dark' }) {
  const light = variant === 'light';
  const scenes = useMemo(
    () => SCENES.map((s) => ({
      ...s,
      line: s.line.replace(/Snag Builder/g, `${brand} Builder`),
    })),
    [brand],
  );
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  const scene = scenes[index];
  const Icon = scene.icon;

  useEffect(() => {
    if (!playing) return undefined;
    setProgress(0);
    const started = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - started) / SCENE_MS);
      setProgress(t);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setIndex((i) => (i + 1) % scenes.length);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [index, playing, scenes.length]);

  const jump = useCallback((i) => {
    setIndex(i);
    setProgress(0);
  }, []);

  return (
    <div
      className={`relative max-w-4xl mx-auto ${className}`}
      data-testid="feature-explainer-video"
    >
      <div className={`absolute inset-0 blur-3xl rounded-3xl ${
        light
          ? 'bg-gradient-to-r from-[#FF6B4A]/15 via-[#FDBA74]/10 to-[#14B8A6]/15'
          : 'bg-gradient-to-r from-violet-600/20 via-cyan-500/10 to-indigo-600/20'
      }`} />
      <div className={`relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl aspect-[16/10] sm:aspect-video flex flex-col ${
        light
          ? 'border border-stone-200 bg-white'
          : 'border border-white/10 bg-zinc-950/95'
      }`}>
        {/* Title bar */}
        <div className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b shrink-0 ${
          light ? 'border-stone-200 bg-stone-50' : 'border-white/10 bg-white/[0.04]'
        }`}>
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
          </div>
          <div className={`flex-1 mx-2 h-6 rounded-md flex items-center justify-center gap-1.5 px-2 border ${
            light ? 'bg-white border-stone-200' : 'bg-black/40 border-white/5'
          }`}>
            <Sparkles className={`w-3 h-3 shrink-0 ${light ? 'text-[#FF6B4A]' : 'text-violet-400'}`} />
            <span className={`text-[10px] sm:text-[11px] truncate ${light ? 'text-stone-500' : 'text-zinc-400'}`}>
              {brand} · 60-second product tour
            </span>
          </div>
          <span className={`hidden sm:inline-flex items-center gap-1 text-[10px] ${light ? 'text-teal-600' : 'text-emerald-400'}`}>
            <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${light ? 'bg-teal-500' : 'bg-emerald-400'}`} />
            Autoplay
          </span>
        </div>

        {/* Stage */}
        <div className={`relative flex-1 min-h-0 grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] ${
          light ? 'bg-white' : ''
        }`}>
          <div className={`relative p-4 sm:p-6 flex flex-col justify-center text-left border-b sm:border-b-0 sm:border-r ${
            light ? 'border-stone-200' : 'border-white/10'
          }`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={scene.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
              >
                <span className={`inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r ${scene.accent} px-2.5 py-0.5 text-[10px] font-semibold text-white mb-3`}>
                  <Icon className="w-3 h-3" />
                  {scene.chip}
                </span>
                <h3 className={`text-xl sm:text-2xl font-heading font-bold tracking-tight mb-2 ${light ? 'text-stone-900' : 'text-white'}`}>
                  {scene.title}
                </h3>
                <p className={`text-sm leading-relaxed max-w-sm ${light ? 'text-stone-500' : 'text-zinc-400'}`}>
                  {scene.line}
                </p>
              </motion.div>
            </AnimatePresence>

            <div className="flex flex-wrap gap-1.5 mt-5">
              {scenes.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => jump(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === index
                      ? (light ? 'w-8 bg-[#FF6B4A]' : 'w-8 bg-white')
                      : (light ? 'w-3 bg-stone-300 hover:bg-stone-400' : 'w-3 bg-white/20 hover:bg-white/40')
                  }`}
                  aria-label={`Show ${s.title}`}
                />
              ))}
            </div>
          </div>

          <div className={`relative min-h-[160px] sm:min-h-0 ${
            light
              ? 'bg-[radial-gradient(ellipse_at_top,_rgba(255,107,74,0.08),_transparent_55%)]'
              : 'bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.12),_transparent_55%)]'
          }`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={scene.id}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.35 }}
                className="absolute inset-0"
              >
                <FakeUi kind={scene.ui} light={light} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Controls */}
        <div className={`shrink-0 border-t px-3 sm:px-4 py-2.5 ${
          light ? 'border-stone-200 bg-stone-50' : 'border-white/10 bg-black/50'
        }`}>
          <div className={`h-1 rounded-full mb-2 overflow-hidden ${light ? 'bg-stone-200' : 'bg-white/10'}`}>
            <div
              className="h-full bg-gradient-to-r from-[#FF6B4A] to-[#14B8A6] transition-[width] duration-75 ease-linear"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                light
                  ? 'bg-[#FF6B4A] text-white hover:bg-[#f25536]'
                  : 'bg-white text-black hover:bg-zinc-100'
              }`}
              aria-label={playing ? 'Pause' : 'Play'}
              data-testid="feature-explainer-play"
            >
              {playing ? <Pause className="w-3.5 h-3.5 fill-current" /> : <Play className="w-3.5 h-3.5 fill-current ml-0.5" />}
            </button>
            <p className={`text-[11px] truncate ${light ? 'text-stone-500' : 'text-zinc-500'}`}>
              {index + 1}/{scenes.length} · {scene.chip}
            </p>
            <div className="flex-1" />
            <p className={`text-[10px] hidden sm:block ${light ? 'text-stone-400' : 'text-zinc-600'}`}>Build · Grow · Market · Automate · Own</p>
          </div>
        </div>
      </div>
    </div>
  );
}
